import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { FileText, Loader2, Save, Upload, Download, X } from "lucide-react";
import { toast } from "sonner";

export default function Syllabus() {
  const { user } = useAuth();
  const canEdit = hasPerm(user, "manage_settings");
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classId, setClassId] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [content, setContent] = useState("");
  const [file, setFile] = useState("");
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/classes").then((r) => setClasses(r.data)).catch(() => {});
    api.get("/subjects").then((r) => setSubjects(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!classId || !subjectId) { setContent(""); setFile(""); setFileName(""); return; }
    setLoading(true);
    api.get("/syllabus", { params: { class_id: classId, subject_id: subjectId } })
      .then((r) => { const d = r.data || {}; setContent(d.content || ""); setFile(d.file || ""); setFileName(d.file_name || ""); })
      .catch(() => { setContent(""); setFile(""); setFileName(""); })
      .finally(() => setLoading(false));
  }, [classId, subjectId]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== "application/pdf") { toast.error("Please upload a PDF file"); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error("File too large (max 8MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => { setFile(reader.result); setFileName(f.name); };
    reader.readAsDataURL(f);
  };

  const save = async () => {
    if (!classId || !subjectId) { toast.error("Select a class and subject"); return; }
    setSaving(true);
    try {
      await api.put("/syllabus", { class_id: classId, subject_id: subjectId, content, file, file_name: fileName });
      toast.success("Syllabus saved");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const ready = classId && subjectId;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-3xl font-extrabold text-slate-900 flex items-center gap-2"><FileText className="text-indigo-500" size={26} />Syllabus</h1>
        <p className="text-slate-500">Attach a syllabus PDF or write it out, per class and subject.</p>
      </div>

      <div className="flex flex-wrap gap-3">
        <Select value={classId} onValueChange={setClassId}>
          <SelectTrigger className="w-56" data-testid="syllabus-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
          <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.section ? `-${c.section}` : ""}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={subjectId} onValueChange={setSubjectId}>
          <SelectTrigger className="w-56" data-testid="syllabus-subject-select"><SelectValue placeholder="Select subject" /></SelectTrigger>
          <SelectContent>{subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {!ready ? <div className="bg-white rounded-xl border border-slate-200 py-16 text-center text-slate-400">Select a class and subject.</div> :
        loading ? <div className="bg-white rounded-xl border border-slate-200 py-16 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div> : (
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-5">
            <div>
              <Label>Syllabus Document (PDF)</Label>
              {file ? (
                <div className="mt-2 flex items-center gap-3 flex-wrap">
                  <a href={file} download={fileName || "syllabus.pdf"} className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:underline" data-testid="syllabus-download"><Download size={15} />{fileName || "Download PDF"}</a>
                  {canEdit && <Button size="sm" variant="ghost" className="text-rose-500 gap-1" onClick={() => { setFile(""); setFileName(""); }} data-testid="syllabus-remove-file"><X size={14} />Remove</Button>}
                </div>
              ) : canEdit ? (
                <label className="mt-2 flex items-center gap-2 border border-dashed border-slate-300 rounded-lg px-4 py-3 cursor-pointer hover:bg-slate-50 w-fit" data-testid="syllabus-upload-label">
                  <Upload size={16} className="text-slate-500" /><span className="text-sm text-slate-600">Upload PDF</span>
                  <input type="file" accept="application/pdf" className="hidden" onChange={onFile} data-testid="syllabus-file-input" />
                </label>
              ) : <p className="text-sm text-slate-400 mt-1">No document uploaded.</p>}
            </div>
            <div>
              <Label>Syllabus Text (optional)</Label>
              <Textarea value={content} onChange={(e) => setContent(e.target.value)} rows={10} readOnly={!canEdit} placeholder="Write the syllabus content here..." className="mt-2" data-testid="syllabus-content" />
            </div>
            {canEdit && <Button onClick={save} disabled={saving} className="gap-1.5" data-testid="save-syllabus-btn">{saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}Save Syllabus</Button>}
          </div>
        )}
    </div>
  );
}
